import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCompleteServiceFee } from '../UserManagement/MasterDistributorManagement/addMDSlice';
import useTranslation from '../../components/CustomHooks/useTranslation';
import { adminContents } from '../AdminManagement/components/AdminManagementContent';
import MainBusinessAssignedArea from '../../components/Add/BusinessAssignedArea/MainBusinessAssignedArea';
import DropdownButtonForSearch from '../../components/Dropdowns/DropdownButtonForSearch';
import InputField from '../../components/inputfields/InputField';
import NestedDropdown from '../../components/Dropdowns/NestedDropdown';
import useApi from '../../services/useApi';
import InputLabel from '../../components/inputfields/InputLabel';
import PostpaidServiceFeeFields from '../UserManagement/MasterDistributorManagement/Components/PostpaidServiceFeeFields';
import AddInputs from '../../components/Add/AddInputs';
import { showToast } from '../../components/toastMessage/toastSlice';
import { BusinessArea } from '../UserManagement/MasterDistributorManagement/Components/types';
import { RootState } from '../../store';
import CustomModal from '../../components/CustomModal';

interface ServiceFeeAndRoleSelectionProps {
    isTemporaryUser: boolean;
    setEditLoading: (value: boolean) => void;
    onSave?: () => void;
    user_id?: string;
    type?: string
    userType?: string;
    businessAssignedArea?: BusinessArea | null | undefined;
    setIsConfirmationModal?: (value: boolean) => void;
    isConfirmationModal?: boolean;
    intendedUserRole?: string;
    userRole?: string;
    handleApproveRequest?: (selectedUserRole?: string) => Promise<void>;
    submitBtnLoading?: boolean;
    setButtonLoading?: (value: boolean) => void;
}

export interface ServiceFeeAndRoleSelectionRef {
    handleSubmit: () => Promise<void>;
}

interface PostpaidDetail {
    mno_id: string;
    mno_name: string;
    bundle_dispersive_fee_percent: number;
    etopup_dispersive_fee_percent: number;
    child_bundle_dispersive_fee_percent?: number;
    child_etopup_dispersive_fee_percent?: number;
    status: boolean;
}

interface ParentOption {
    label: string;
    value: string;
    isNested?: boolean;
    apiEndpoint?: string;
    id?: string;
}

interface ServiceFeeUpfront {
    service_fee_type: 'Upfront';
    service_fee_parent: string;
    service_fee_parent_name: string;
    service_fee_parent_id: string | undefined;
    upfront_service_fee_percentage: number;
    child_upfront_service_fee_percentage?: number;
}

interface ServiceFeePostpaid {
    service_fee_type: 'Postpaid';
    service_fee_parent: string;
    service_fee_parent_name: string;
    service_fee_parent_id: string | undefined;
    service_fee_details: PostpaidDetail[];
}

const ServiceFeeAndRoleSelection = forwardRef<ServiceFeeAndRoleSelectionRef, ServiceFeeAndRoleSelectionProps>(({
    isTemporaryUser,
    setEditLoading,
    onSave,
    user_id,
    type,
    userType,
    businessAssignedArea,
    setIsConfirmationModal,
    isConfirmationModal,
    intendedUserRole,
    userRole,
    handleApproveRequest,
    submitBtnLoading,
    setButtonLoading
}, ref) => {
    const { translate } = useTranslation(adminContents);
    const dispatch = useDispatch();
    const { getAPI, PatchAPI } = useApi();
    const [selectedUserRole, setSelectedUserRole] = useState<string>('');
    const [selectedParent, setSelectedParent] = useState<string>('');
    const [serviceFeeType, setServiceFeeType] = useState<string>('');
    const [showBusinessArea, setShowBusinessArea] = useState<boolean>(false);
    const [upfrontPercentage, setUpfrontPercentage] = useState<number | string>('');
    const [serviceFeeDetails, setServiceFeeDetails] = useState<PostpaidDetail[]>([]);
    const [localErrors, setLocalErrors] = useState<{ [key: string]: string }>({});
    const [mdDetails, setMdDetails] = useState<any>(null);
    const [childPostpaidPercentage, setChildPostpaidPercentage] = useState<{
        [key: number]: {
            child_bundle_dispersive_fee_percent: string | number;
            child_etopup_dispersive_fee_percent: string | number;
        };
    }>({});
    const [childPostpaidErrors, setChildPostpaidErrors] = useState<{
        [key: number]: {
            child_bundle_dispersive_fee_percent?: string;
            child_etopup_dispersive_fee_percent?: string;
        };
    }>({});
    const [mnoList, setMnoList] = useState<any[]>([]);
    const [parentMDSelected, setParentMDSelected] = useState<boolean>(false);
    const [childUpfrontFeePercentage, setChildUpfrontFeePercentage] = useState<string | number>('');
    const [childUpfrontError, setChildUpfrontError] = useState<{ child_upfront_service_fee_percentage: string }>({
        child_upfront_service_fee_percentage: ''
    });
    const [maxUpfrontServiceFeePercent, setMaxUpfrontServiceFeePercent] = useState(0);
    const { region, provinces } = useSelector((state: RootState) => state.businessAreas.areas);
    const service_fee = useSelector((state: RootState) => state.addMD.service_fee);

    const userRoleMap: any = {
        'MDST': ['Master Distributor'],
        'DST': ['Distributor'],
        'SDST': ['Sub Distributor'],
        'AGT': ['Agent'],
        'RET': ['Retailer'],
    };
    const extractParentCode = (parent: string) => {
        if (parent === "ZoodPay") return "ZoodPay";

        // Extract the code from within parentheses
        const codeMatch = parent.match(/\(([^)]+)\)/);
        if (codeMatch) {
            const userCode = codeMatch[1];
            // Extract the prefix (letters before numbers) from the user code
            const prefixMatch = userCode.match(/^([A-Za-z]+)/);
            if (prefixMatch) {
                return prefixMatch[1]; // Return just the prefix (e.g., "MDST" from "MDST325126")
            }
        }
        return parent;
    };
    // Add useEffect to check for service fee data in Redux
    useEffect(() => {
        if (Object.keys(service_fee).length > 0) {
            if (service_fee.service_fee_parent) {
                setSelectedParent(service_fee.service_fee_parent === 'ZoodPay' ? service_fee.service_fee_parent : service_fee.service_fee_parent?.split(' ').length === 1 ? service_fee.service_fee_parent_name : service_fee.service_fee_parent);
            }
            if (service_fee.service_fee_parent !== 'ZoodPay') {
                setParentMDSelected(true);
            }
            setServiceFeeType(service_fee.service_fee_type);
            setSelectedUserRole(userRoleMap[intendedUserRole || service_fee.user_role || 0]?.[0] || '');

            if (service_fee.service_fee_type === 'Upfront' && service_fee.service_fee_parent !== 'ZoodPay') {
                if(service_fee.child_upfront_service_fee_percentage !== undefined) {
                    setChildUpfrontFeePercentage(service_fee.child_upfront_service_fee_percentage);
                } else if (service_fee.upfront_service_fee_percentage !== undefined) {
                    setChildUpfrontFeePercentage(service_fee.upfront_service_fee_percentage);
                }
                setMaxUpfrontServiceFeePercent(service_fee.upfront_service_fee_percentage !== undefined ? service_fee.upfront_service_fee_percentage : 0);
                setParentMDSelected(true);
            } else if (service_fee.service_fee_type === 'Upfront' && service_fee.service_fee_parent === 'ZoodPay') {
                setUpfrontPercentage(service_fee.upfront_service_fee_percentage !== undefined ? service_fee.upfront_service_fee_percentage : '');
                setParentMDSelected(false);
            } else if (service_fee.service_fee_type === 'Postpaid' && service_fee.service_fee_parent !== 'ZoodPay') {
                const details = service_fee.child_service_fee_details || [];
                setServiceFeeDetails(details.map(detail => ({
                    mno_id: detail.mno_id,
                    mno_name: detail.mno_name || '',
                    bundle_dispersive_fee_percent: detail.bundle_dispersive_fee_percent,
                    etopup_dispersive_fee_percent: detail.etopup_dispersive_fee_percent,
                    child_bundle_dispersive_fee_percent: detail.child_bundle_dispersive_fee_percent,
                    child_etopup_dispersive_fee_percent: detail.child_etopup_dispersive_fee_percent,
                    status: detail.status
                })));

                // Set child postpaid percentages if they exist
                if (service_fee.child_service_fee_details) {
                    const childPercentages: {
                        [key: number]: {
                            child_bundle_dispersive_fee_percent: string | number;
                            child_etopup_dispersive_fee_percent: string | number;
                        };
                    } = {};

                    service_fee.child_service_fee_details.forEach((detail, index) => {
                        childPercentages[index] = {
                            child_bundle_dispersive_fee_percent: detail.child_bundle_dispersive_fee_percent !== undefined ? detail.child_bundle_dispersive_fee_percent : '',
                            child_etopup_dispersive_fee_percent: detail.child_etopup_dispersive_fee_percent !== undefined ? detail.child_etopup_dispersive_fee_percent : ''
                        };
                    });

                    setChildPostpaidPercentage(childPercentages);
                    setParentMDSelected(true);
                }
            } else if (service_fee.service_fee_type === 'Postpaid' && service_fee.service_fee_parent === 'ZoodPay') {
                setServiceFeeDetails(service_fee.service_fee_details || []);
            }
            // Only fetch MD details if we don't already have them and we're not in a submission flow
            if (service_fee.service_fee_parent_id && service_fee.service_fee_parent !== 'ZoodPay' && !mdDetails) {
                const parentType = userRoleMap[extractParentCode(service_fee.service_fee_parent)]?.[0].toLowerCase().replace(/ /g, '-') + 's';
                const fetchDetails = async () => {
                    try {
                        const mdDetailsResponse = await fetchMDDetails(service_fee?.service_fee_parent_id, parentType);
                        if (mdDetailsResponse) {
                            setMdDetails(mdDetailsResponse);
                            const serviceType = mdDetailsResponse?.data?.service_fee?.service_fee_type;
                            setServiceFeeType(serviceType);
                            setParentMDSelected(true);

                            if (serviceType === 'Upfront') {
                                setMaxUpfrontServiceFeePercent(mdDetailsResponse?.data?.service_fee?.upfront_service_fee_percentage);
                            } else if (serviceType === 'Postpaid') {
                                const details: PostpaidDetail[] = mdDetailsResponse?.data?.service_fee?.service_fee_details || [];
                                const childDetails = service_fee?.service_fee_details || [];
                                setServiceFeeDetails(details.map((detail: PostpaidDetail) => {
                                    const matchedChild = childDetails.find(child => child.mno_id === detail.mno_id);

                                    return {
                                        mno_id: detail.mno_id,
                                        mno_name: detail.mno_name || '',
                                        bundle_dispersive_fee_percent: detail.bundle_dispersive_fee_percent,
                                        etopup_dispersive_fee_percent: detail.etopup_dispersive_fee_percent,
                                        child_bundle_dispersive_fee_percent: matchedChild?.bundle_dispersive_fee_percent,
                                        child_etopup_dispersive_fee_percent: matchedChild?.etopup_dispersive_fee_percent,
                                        status: detail.status
                                    };
                                }));
                            }

                        }
                    } catch (error) {
                        console.error('Error fetching MD details:', error);
                    }
                };
                fetchDetails();
            }
        }
    }, [service_fee]);
    useEffect(() => {
        if (service_fee) {
            if (
                service_fee.service_fee_type === "Upfront" &&
                service_fee.service_fee_parent === "ZoodPay"
            ) {
                handleMaxUpfrontPercentage();
            }
        }
    }, []);



    const userRoles = userType
        ? userType.split('/').flatMap(type => userRoleMap[type] || [])
        : ['Master Distributor', 'Distributor', 'Sub Distributor', 'Agent', 'Retailer'];

    const serviceFeeTypes = [
        'Upfront',
        'Postpaid'
    ];
    const getParentOptions = (): ParentOption[] => {
        const options: ParentOption[] = [{ label: "ZoodPay", value: "ZoodPay" }];

        switch (selectedUserRole || type) {
            case 'Master Distributor':
                return options;
            case 'Distributor':
                options.push({
                    label: "Master Distributor",
                    value: "Master Distributor",
                    isNested: true,
                    apiEndpoint: "master-distributors",
                    id: "master-distributor"
                });
                break;
            case 'Sub Distributor':
                options.push(
                    {
                        label: "Master Distributor",
                        value: "Master Distributor",
                        isNested: true,
                        apiEndpoint: "master-distributors",
                        id: "master-distributor"
                    },
                    {
                        label: "Distributor",
                        value: "Distributor",
                        isNested: true,
                        apiEndpoint: "distributors",
                        id: "distributor"
                    }
                );
                break;
            case 'Agent':
                options.push(
                    {
                        label: "Master Distributor",
                        value: "Master Distributor",
                        isNested: true,
                        apiEndpoint: "master-distributors",
                        id: "master-distributor"
                    },
                    {
                        label: "Distributor",
                        value: "Distributor",
                        isNested: true,
                        apiEndpoint: "distributors",
                        id: "distributor"
                    },
                    {
                        label: "Sub Distributor",
                        value: "Sub Distributor",
                        isNested: true,
                        apiEndpoint: "sub-distributors",
                        id: "sub-distributor"
                    }
                );
                break;
            case 'Retailer':
                options.push(
                    {
                        label: "Master Distributor",
                        value: "Master Distributor",
                        isNested: true,
                        apiEndpoint: "master-distributors",
                        id: "master-distributor"
                    },
                    {
                        label: "Distributor",
                        value: "Distributor",
                        isNested: true,
                        apiEndpoint: "distributors",
                        id: "distributor"
                    },
                    {
                        label: "Sub Distributor",
                        value: "Sub Distributor",
                        isNested: true,
                        apiEndpoint: "sub-distributors",
                        id: "sub-distributor"
                    },
                    {
                        label: "Agent",
                        value: "Agent",
                        isNested: true,
                        apiEndpoint: "agents",
                        id: "agent"
                    }
                );
                break;
        }

        return options;
    };

    const handleUserRoleChange = (id: string, value: string) => {
        // Only reset fields if the user role has actually changed
        if (value !== selectedUserRole) {
            setSelectedUserRole(value);
            setSelectedParent('');
            setParentMDSelected(false);
            setServiceFeeType('');
            setUpfrontPercentage('');
            setServiceFeeDetails([]);
            setChildUpfrontFeePercentage('');
            setChildUpfrontError({ child_upfront_service_fee_percentage: '' });
            setLocalErrors({});
            setMdDetails(null);
            updateServiceFee();
        }
    };

    const fetchMDDetails = async (mdId: string, parentType: string) => {
        try {
            const response = await getAPI(`${parentType}/${mdId}?assign_fee_mode=true`);
            if (!response.error) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error("Error fetching user details:", error);
            return null;
        }
    };

    const handleMDSelection = async (userData: any) => {
        if (userData && userData.id) {
            const mdDetailsResponse = await fetchMDDetails(userData.id, userData.type);
            if (mdDetailsResponse) {
                setMdDetails(mdDetailsResponse);
                const serviceType = mdDetailsResponse.data.service_fee.service_fee_type;
                setServiceFeeType(serviceType);
                setParentMDSelected(true);

                if (serviceType === 'Upfront') {
                    setMaxUpfrontServiceFeePercent(mdDetailsResponse.data.service_fee.upfront_service_fee_percentage);
                } else if (serviceType === 'Postpaid') {
                    const details: PostpaidDetail[] = mdDetailsResponse.data.service_fee.service_fee_details || [];
                    setServiceFeeDetails(details.map((detail: PostpaidDetail) => ({
                        mno_id: detail.mno_id,
                        mno_name: detail.mno_name || '',
                        bundle_dispersive_fee_percent: detail.bundle_dispersive_fee_percent,
                        etopup_dispersive_fee_percent: detail.etopup_dispersive_fee_percent,
                        status: detail.status
                    })));
                }
            }
        }
    };

    const handleParentChange = (userData: any) => {
        if (userData === "ZoodPay") {
            setSelectedParent("ZoodPay");
            setParentMDSelected(false);
            setServiceFeeType('');
            setMaxUpfrontServiceFeePercent(0);
            setUpfrontPercentage('');
            setServiceFeeDetails([]);
        } else {
            setSelectedParent(userData.user_name);
            handleMDSelection(userData);
        }
        setLocalErrors(prev => ({ ...prev, parent: '' }));
        updateServiceFee();
    };

    const handleMaxUpfrontPercentage = async () => {
        try {
            let response = await getAPI(`mnos/?max_upfront_fee=true`);
            setMaxUpfrontServiceFeePercent(response?.data.max_upfront_service_fee_percent || 0);
        } catch (error) {
            console.error('Error fetching max upfront percentage:', error);
        }
    };

    const handleServiceFeeTypeChange = (id: string, value: string) => {
        setServiceFeeType(value);
        setUpfrontPercentage('');
        setServiceFeeDetails([]);
        setChildUpfrontFeePercentage('');
        setChildUpfrontError({ child_upfront_service_fee_percentage: '' });
        setLocalErrors(prev => ({ ...prev, service_fee_type: '' }));
        if (value === 'Upfront') {
            handleMaxUpfrontPercentage();
        }
        updateServiceFee();
    };

    const handleUpfrontPercentageChange = (id: string, value: string) => {
        // const numValue = parseFloat(value);
        setUpfrontPercentage(value);
        setLocalErrors(prev => ({ ...prev, upfront_percentage: '' }));
        updateServiceFee();
    };

    const handleChildUpfrontServiceFeePercentage = (id: string, value: string | number) => {
        setChildUpfrontFeePercentage(value);
        setChildUpfrontError({ child_upfront_service_fee_percentage: '' });
        setLocalErrors(prev => ({ ...prev, child_upfront_service_fee_percentage: '' }));
        updateServiceFee();
    };

    const updateServiceFee = () => {
    };

    const fetchMasterDistributors = async (endpoint: string, searchTerm: string, page: number = 1) => {
        try {
            const response = await getAPI(`${endpoint}?${searchTerm ? `search=${searchTerm}&` : ''}page_number=${page}&assign_fee_mode=true`);
            return {
                data: {
                    users: response.data.data.map((user: any) => ({
                        user_name: `${user.first_name || ''} ${user.last_name || ''} ${user.user_code && `(${user.user_code})`}`.trim(),
                        user_code: user.user_code || '',
                        status: user.service_fee_type || 'N/A',
                        id: user.id,
                        type: endpoint
                    })),
                    total_count: response.data.total_count
                }
            };
        } catch (error) {
            console.error('Error fetching master distributors:', error);
            return { data: { users: [], total_count: 0 } };
        }
    };

    const handleServiceFeeDetailsChange = (details: any[]) => {
        const convertedDetails = details.map(detail => ({
            ...detail,
            bundle_dispersive_fee_percent: detail.bundle_dispersive_fee_percent,
            etopup_dispersive_fee_percent: detail.etopup_dispersive_fee_percent,
            child_bundle_dispersive_fee_percent: detail.child_bundle_dispersive_fee_percent ? detail.child_bundle_dispersive_fee_percent : undefined,
            child_etopup_dispersive_fee_percent: detail.child_etopup_dispersive_fee_percent ? detail.child_etopup_dispersive_fee_percent : undefined
        }));
        setServiceFeeDetails(convertedDetails);
        const newErrors = { ...localErrors };
        Object.keys(newErrors).forEach(key => {
            if (key.startsWith('postpaid_')) {
                delete newErrors[key];
            }
        });
        delete newErrors.general;
        setLocalErrors(newErrors);
    };

    const handleChildPostpaidPercentageChange = (percentages: {
        [key: number]: {
            child_bundle_dispersive_fee_percent: string | number;
            child_etopup_dispersive_fee_percent: string | number;
        };
    }) => {
        setChildPostpaidPercentage(percentages);
        // Clear child postpaid errors when values are updated
        const newErrors = { ...childPostpaidErrors };
        Object.keys(percentages).forEach(key => {
            const numKey = Number(key);
            if (newErrors[numKey]) {
                delete newErrors[numKey];
            }
        });
        setChildPostpaidErrors(newErrors);
    };

    const userRoleInput = {
        "": {
            "User Role": {
                id: "user_role",
                type: "dropDown",
                required: true,
                noSearch: true,
                options: userRoles,
                tabIndex: 1,
                disabled: false,
                width: 'w-full'
            }
        }
    };

    const serviceFeeTypeInput = {
        "": {
            [(parentMDSelected ? "Parent Service Fee Type" : "Service Fee Type")]: {
                id: "service_fee_type",
                type: "dropDown",
                required: true,
                noSearch: true,
                options: serviceFeeTypes,
                tabIndex: 3,
                disabled: parentMDSelected ? true : false,
                width: "w-full",
            },
        },
    };

    const upfrontPercentageInput = {
        "": {
            [(parentMDSelected ? "Parent Service Fee Percentage" : "Upfront Service Fee Percentage")]: {
                id: "upfront_percentage",
                type: "numberInput",
                required: true,
                maxLength: 60,
                tabIndex: 4,
                percentage: true,
                placeholder: 'Enter percentage',
                min: 0,
                max: 100,
                decimalPlaces: 2,
                disabled: parentMDSelected,
                secondaryText: !parentMDSelected ? `Note : Percentage must not exceed ${maxUpfrontServiceFeePercent?.toFixed(2)}%` : undefined,
                value: upfrontPercentage.toString()
            }
        }
    };
    
    const childUpfrontPercentageInput = {
        "": {
            "Child Service Fee Percentage": {
                id: "child_upfront_service_fee_percentage",
                type: "numberInput",
                required: true,
                maxLength: 60,
                tabIndex: 5,
                percentage: true,
                placeholder: 'Enter percentage',
                min: 0,
                max: 100,
                decimalPlaces: 2,
                secondaryText: `Note : Percentage must not exceed ${maxUpfrontServiceFeePercent}%`,
                disabled: false
            }
        }
    };

    const validateServiceFee = () => {
        const errors: { [key: string]: string } = {};
        let childErrors: {
            [key: number]: {
                child_bundle_dispersive_fee_percent?: string;
                child_etopup_dispersive_fee_percent?: string;
            };
        } = {};
        if (!selectedUserRole && userType) {
            errors.user_role = "This field is required";
        }
        if (!serviceFeeType) {
            errors.service_fee_type = "This field is required";
        }
        if (!selectedParent && selectedUserRole !== 'Master Distributor') {
            errors.parent = "This field is required"
        }

        if (serviceFeeType === "Upfront") {
            if (!parentMDSelected) {
                if (upfrontPercentage === '' || upfrontPercentage === null || upfrontPercentage === undefined) {
                    errors.upfront_percentage = "This field is required";
                } else if (Number(upfrontPercentage) < 0) {
                    errors.upfront_percentage = "Percentage must be greater than or equal to 0";
                }
                if (Number(upfrontPercentage) > Number(maxUpfrontServiceFeePercent)) {
                    errors.upfront_percentage = `Percentage must not exceed ${maxUpfrontServiceFeePercent?.toFixed(2)}%`;
                }
            }

            if (parentMDSelected) {
                if (childUpfrontFeePercentage === '' || childUpfrontFeePercentage === null || childUpfrontFeePercentage === undefined) {
                    errors.child_upfront_service_fee_percentage = "This field is required";
                } else if (Number(childUpfrontFeePercentage) < 0) {
                    errors.child_upfront_service_fee_percentage = "Percentage must be greater than or equal to 0";
                }
                const childPercentage = Number(childUpfrontFeePercentage);
                if (Number(childPercentage) > Number(maxUpfrontServiceFeePercent)) {
                    errors.child_upfront_service_fee_percentage = `Percentage must not exceed ${maxUpfrontServiceFeePercent}%`;
                }
            }
        } else if (serviceFeeType === "Postpaid") {

            serviceFeeDetails.forEach((detail: any, index) => {
                if (!detail.mno_id) {
                    errors[`postpaid_${index}_mno_id`] = "This field is required";
                }
                if (Number(detail.bundle_dispersive_fee_percent) < 0) {
                    errors[`postpaid_${index}_bundle`] = "Percentage must be greater than or equal to 0";
                }
                if (Number(detail.etopup_dispersive_fee_percent) < 0) {
                    errors[`postpaid_${index}_etopup`] = "Percentage must be greater than or equal to 0";
                }
                if (!detail.bundle_dispersive_fee_percent) {
                    errors[`postpaid_${index}_bundle`] = "This field is required";
                }
                if (!detail.etopup_dispersive_fee_percent) {
                    errors[`postpaid_${index}_etopup`] = "This field is required";
                }

                // Validate child percentages if parent is selected
                if (parentMDSelected) {
                    const childBundlePercent = detail?.child_bundle_dispersive_fee_percent;
                    const childEtopupPercent = detail?.child_etopup_dispersive_fee_percent;

                    if (!childErrors[index]) {
                        childErrors[index] = {};
                    }

                    if (childBundlePercent === undefined || childBundlePercent === '') {
                        childErrors[index].child_bundle_dispersive_fee_percent = "This field is required";
                    } else if (Number(childBundlePercent) < 0) {
                        childErrors[index].child_bundle_dispersive_fee_percent = "Percentage must be greater than or equal to 0";
                    } else if (Number(childBundlePercent) > Number(detail.bundle_dispersive_fee_percent)) {
                        childErrors[index].child_bundle_dispersive_fee_percent = `Percentage must not exceed ${detail.bundle_dispersive_fee_percent}%`;
                    }

                    if (childEtopupPercent === undefined || childEtopupPercent === '') {
                        childErrors[index].child_etopup_dispersive_fee_percent = "This field is required";
                    } else if (Number(childEtopupPercent) < 0) {
                        childErrors[index].child_etopup_dispersive_fee_percent = "Percentage must be greater than or equal to 0";
                    } else if (Number(childEtopupPercent) > Number(detail.etopup_dispersive_fee_percent)) {
                        childErrors[index].child_etopup_dispersive_fee_percent = `Percentage must not exceed ${detail.etopup_dispersive_fee_percent}%`;
                    }

                    setChildPostpaidErrors(childErrors);
                }
            });
        }

        return { errors, childErrors };
    };

    const handleSubmit = async () => {
        const { errors, childErrors } = validateServiceFee();
        if (Object.keys(errors).length > 0 || (childErrors && Object.values(childErrors).some(error => Object.keys(error).length > 0))) {
            setLocalErrors(errors);
            setChildPostpaidErrors(childErrors)
            return;
        }

        // Check for child postpaid errors
        if (serviceFeeType === "Postpaid" && parentMDSelected) {
            const hasChildErrors = Object.values(childPostpaidErrors).some(error =>
                error.child_bundle_dispersive_fee_percent || error.child_etopup_dispersive_fee_percent
            );
            if (hasChildErrors) {
                return;
            }
        }
        let payload: any = {};

        // Add business assigned area if it exists
        if (region && provinces.length > 0) {
            payload.basic_details = {
                business_assigned_area: {
                    region,
                    provinces
                }
            };
        }

        let reduxData;

        let updatedUserRole = selectedUserRole === "Master Distributor" ? "MDST"
            : selectedUserRole === "Distributor" ? "DST"
                : selectedUserRole === "Sub Distributor" ? "SDST"
                    : selectedUserRole === "Agent" ? "AGT"
                        : selectedUserRole === "Retailer" ? "RET"
                            : selectedUserRole

        if (serviceFeeType === "Upfront") {
            const serviceFeeData: ServiceFeeUpfront = {
                service_fee_type: "Upfront",
                user_role: updatedUserRole,
                service_fee_parent: extractParentCode(selectedParent) || 'ZoodPay',
                service_fee_parent_name: selectedParent,
                service_fee_parent_id: selectedParent !== "ZoodPay" ? mdDetails?.data?.id : undefined,
                upfront_service_fee_percentage: parentMDSelected ? Number(childUpfrontFeePercentage) : Number(upfrontPercentage),
            };
            const { service_fee_parent_name, ...apiPayload } = serviceFeeData;
            payload = {
                ...payload,
                ...(isTemporaryUser && { user_role: updatedUserRole }),
                service_fee: apiPayload
            };

            // Create a copy for Redux state with all data
            reduxData = {
                ...serviceFeeData,
                upfront_service_fee_percentage: parentMDSelected ? Number(maxUpfrontServiceFeePercent) : Number(upfrontPercentage),
                child_upfront_service_fee_percentage: parentMDSelected ? Number(childUpfrontFeePercentage) : undefined
            };
        } else if (serviceFeeType === "Postpaid") {
            const serviceFeeData: ServiceFeePostpaid = {
                service_fee_type: "Postpaid",
                user_role: updatedUserRole,
                service_fee_parent: extractParentCode(selectedParent) || 'ZoodPay',
                service_fee_parent_name: selectedParent,
                service_fee_parent_id: selectedParent !== "ZoodPay" ? mdDetails?.data?.id : undefined,
                service_fee_details: serviceFeeDetails.map(detail => ({
                    mno_id: detail.mno_id,
                    bundle_dispersive_fee_percent: selectedParent !== 'ZoodPay' ? Number(detail.child_bundle_dispersive_fee_percent) : Number(detail.bundle_dispersive_fee_percent),
                    etopup_dispersive_fee_percent: selectedParent !== 'ZoodPay' ? Number(detail.child_etopup_dispersive_fee_percent) : Number(detail.etopup_dispersive_fee_percent),
                    status: detail.status
                }))
            };
            const { service_fee_parent_name, ...apiPayload } = serviceFeeData;
            payload = {
                ...payload,
                ...(isTemporaryUser && { user_role: updatedUserRole }),
                service_fee: apiPayload
            };

            // Create a copy for Redux state with all data
            reduxData = {
                ...serviceFeeData,
                child_service_fee_details: parentMDSelected ? serviceFeeDetails.map(detail => ({
                    mno_id: detail.mno_id,
                    mno_name: detail.mno_name || '',
                    bundle_dispersive_fee_percent: Number(detail.bundle_dispersive_fee_percent),
                    etopup_dispersive_fee_percent: Number(detail.etopup_dispersive_fee_percent),
                    child_bundle_dispersive_fee_percent: detail.child_bundle_dispersive_fee_percent ? Number(detail.child_bundle_dispersive_fee_percent) : undefined,
                    child_etopup_dispersive_fee_percent: detail.child_etopup_dispersive_fee_percent ? Number(detail.child_etopup_dispersive_fee_percent) : undefined,
                    status: detail.status
                })) : undefined
            };
        }
        setButtonLoading?.(true)

        try {
            const response = await PatchAPI(
                `${type?.toLowerCase().replace(/ /g, "-")}s/${user_id}`,
                payload
            );
            if (!response?.error) {
                // Store complete data in Redux after successful API call
                dispatch(setCompleteServiceFee(reduxData));
                dispatch(showToast({ message: "Details updated successfully", type: "success" }));
                setIsConfirmationModal?.(true)
                if (onSave) {
                    onSave();
                }
                setButtonLoading?.(false)
            } else {
                const errorMsg = response?.data?.response?.data?.message || 'Something went wrong';
                setLocalErrors({ general: errorMsg });
                dispatch(showToast({ message: errorMsg, type: "error" }));
                setButtonLoading?.(false)
            }
        } catch (error) {
            console.error('API Error:', error);
            setLocalErrors({ general: 'An error occurred while saving service fee details' });
            dispatch(showToast({ message: "An error occurred while saving details", type: "error" }));
            setButtonLoading?.(false)
        }
    };



    useImperativeHandle(ref, () => ({
        handleSubmit: async () => {
            await handleSubmit();
        }
    }));

    return (
        <div className="w-full">
            <h1 className="text-xl font-semibold text-primary underline pb-2">{translate('Service Fee ')}</h1>
            <div className="grid grid-cols-3 gap-4 mb-6 w-full">
                {isTemporaryUser && (
                    <AddInputs
                        inputs={userRoleInput}
                        totalInputCount={1}
                        inputValues={{
                            "user_role": selectedUserRole
                        }}
                        onInputChange={(id: string, value: string) => {
                            handleUserRoleChange(id, value);
                        }}
                        errors={localErrors}
                    />
                )}

                {((selectedUserRole !== 'Master Distributor' && isTemporaryUser) || userRole !== 'Master Distributor') && (
                    <div className="w-full">
                        <InputLabel labelName={'Parent User'} id={''} required={true} />
                        <NestedDropdown
                            options={getParentOptions()}
                            onSelect={handleParentChange}
                            selectedData={selectedParent}
                            fetchUserData={fetchMasterDistributors}
                            testId="parent-user-dropdown"
                            errorMessage={localErrors.parent}
                            key={selectedUserRole}
                        />
                    </div>
                )}

                <AddInputs
                    inputs={serviceFeeTypeInput}
                    totalInputCount={1}
                    inputValues={{
                        "service_fee_type": serviceFeeType
                    }}
                    onInputChange={(id: string, value: string) => {
                        handleServiceFeeTypeChange(id, value);
                    }}
                    errors={localErrors}
                />

                {((!isTemporaryUser || selectedUserRole === 'Master Distributor') && serviceFeeType === 'Upfront') && (
                    <AddInputs
                        inputs={upfrontPercentageInput}
                        totalInputCount={1}
                        inputValues={{
                            "upfront_percentage": parentMDSelected ? maxUpfrontServiceFeePercent : upfrontPercentage.toString()
                        }}
                        onInputChange={handleUpfrontPercentageChange}
                        mapping={true}
                        isUpfrontFee={true}
                        errors={localErrors}
                    />
                )}
            </div>

            {(isTemporaryUser && selectedUserRole !== 'Master Distributor' && serviceFeeType === 'Upfront') ||
                (parentMDSelected && serviceFeeType === 'Upfront') ? (
                <div className="grid grid-cols-3 gap-4 mb-6 w-full">
                    {isTemporaryUser && selectedUserRole !== 'Master Distributor' && serviceFeeType === 'Upfront' && (
                        <AddInputs
                            inputs={upfrontPercentageInput}
                            totalInputCount={1}
                            inputValues={{
                                "upfront_percentage": parentMDSelected ? maxUpfrontServiceFeePercent : upfrontPercentage.toString()
                            }}
                            onInputChange={handleUpfrontPercentageChange}
                            mapping={true}
                            isUpfrontFee={true}
                            errors={localErrors}
                        />
                    )}

                    {parentMDSelected && serviceFeeType === 'Upfront' && (
                        <AddInputs
                            inputs={childUpfrontPercentageInput}
                            totalInputCount={1}
                            inputValues={{
                                "child_upfront_service_fee_percentage": childUpfrontFeePercentage.toString()
                            }}
                            onInputChange={(id: string, value: string) => {
                                handleChildUpfrontServiceFeePercentage(id, value);
                            }}
                            mapping={true}
                            isUpfrontFee={true}
                            errors={localErrors}
                        />
                    )}
                </div>
            ) : null}

            {serviceFeeType === 'Postpaid' && (
                <PostpaidServiceFeeFields
                    service_fee_details={serviceFeeDetails}
                    parentMDSelected={parentMDSelected}
                    type={selectedUserRole}
                    onServiceFeeDetailsChange={handleServiceFeeDetailsChange}
                    onErrorsChange={setLocalErrors}
                    localErrors={localErrors}
                    childPostpaidPercentage={childPostpaidPercentage}
                    onChildPostpaidPercentageChange={handleChildPostpaidPercentageChange}
                    childPostpaidErrors={childPostpaidErrors}
                    onChildPostpaidErrorsChange={setChildPostpaidErrors}
                    onMnoListChange={setMnoList}
                />
            )}

            {(!businessAssignedArea || Object.keys(businessAssignedArea).length === 0) && (
                <div className="mt-6">
                    <MainBusinessAssignedArea />
                </div>
            )}
            {isConfirmationModal && (
                <CustomModal
                    isOpen={isConfirmationModal}
                    onClose={() => {
                        setIsConfirmationModal?.(false);
                    }}
                    title="Approve Request"
                    subText={'Are you sure you want to approve this onboarding request?'}
                    showCloseButton={false}
                    primaryButtonDisabled={false}
                    secondaryButtonDisabled={false}
                    primaryButtonText="Yes"
                    buttonLoading={submitBtnLoading}
                    secondaryButtonText="No"
                    handelSubmit={() => {
                        handleApproveRequest?.()
                    }}
                    maxWidth="max-w-[461px]"
                    buttonWidth="w-[65px]"
                />
            )}
        </div>
    );
});

export default ServiceFeeAndRoleSelection; 